import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import hash from 'object-hash'

import { Palette } from '../'
import { mergeDeepAll } from '../utils'
import {
  CompassAPIDataBase,
  CompassAPIDispersionDataPoint,
  CompassAPIMultiSeriesChartData,
  DataSource,
  getDataSources,
  getMultipleLineChartProps,
  magnitudePointFormatter,
} from '../utils/compass'
import { formatSeriesNames } from '../utils/string'
import type { BaseContext } from './'
import { commonChartOptions } from './common'

interface CategoryPoint {
  label: string
  value: number
}

interface CategoryChartData extends CompassAPIDataBase {
  title?: string
  data: CategoryPoint[]
}

const DEFAULT_Y_AXIS = {
  endOnTick: false,
  id: 'default',
  opposite: false,
  startOnTick: false,
  title: { text: '' },
}

const PERCENTAGE_Y_AXIS = {
  ...DEFAULT_Y_AXIS,
  labels: { format: '{value}%' },
}

export interface CompassAPIScatterChartData {
  data: CompassAPIDispersionDataPoint[]
}

export namespace Surprise {
  export namespace Actual {
    type ActualChartId =
      | 'quarterly_infos'
      | 'coverage_factor'
      | 'top_bottom'
      | 'historical_surprise'
      | 'growth_yoy_reported'
      | 'percentage_positive_surprises'
      | 'by_ticker'
      | 'by_sector'

    const ACTUAL_CHART_PATHS: Record<ActualChartId, string> = {
      quarterly_infos: '/surprises/quarterly-infos',
      historical_surprise: '/surprises/historical-surprise',
      top_bottom: '/surprises/top-bottom',
      percentage_positive_surprises: '/surprises/percentage-positive-surprises',
      growth_yoy_reported: '/surprises/growth-yoy-reported',
      coverage_factor: '/surprises/coverage-factor',
      by_ticker: '/surprises/by-ticker',
      by_sector: '/surprises/by-sector',
    }

    const CHART_TITLES: Record<ActualChartId, string> = {
      quarterly_infos: 'Earnings surprise',
      coverage_factor: 'Coverage factor',
      top_bottom: 'Earnings IBOV surprise - points',
      historical_surprise: 'Historical surprise',
      growth_yoy_reported: 'Growth YoY',
      percentage_positive_surprises: 'Positive surprise',
      by_ticker: 'Earnings surprise - <Sector>',
      by_sector: 'Sector sentiment - <Quarter>',
    }

    const CONTEXT_ID = 'equity:surprise:actual'
    const SURPRISE_CHARTS_PATH = '/api/charts/surprise'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      context: typeof CONTEXT_ID
      chart: ActualChartId
      requestId: string
      sector?: string
      quarter?: string
      title?: string
    }

    interface TopBottomData extends CompassAPIDataBase {
      data: { bottom: CategoryPoint[]; top: CategoryPoint[] }
    }

    type ChartData =
      | CompassAPIMultiSeriesChartData
      | CategoryChartData
      | TopBottomData

    const getChartData = async (
      config: ChartConfig,
      modifier: (config: AxiosRequestConfig) => AxiosRequestConfig = (conf) =>
        conf
    ) => {
      const response = (await axios(
        modifier({
          url: SURPRISE_CHARTS_PATH + ACTUAL_CHART_PATHS[config.chart],
          method: 'GET',
          params: {
            ...(config.sector ? { sector: config.sector } : {}),
            request_id: config.requestId,
          },
        })
      )) as AxiosResponse<ChartData>

      return response.data
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.chart}:${hash(config)}`,
      getChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'quarterly_infos':
          case 'by_ticker':
          case 'by_sector':
            return getQuarterlyChartProps(config, data as CategoryChartData)
          case 'top_bottom':
            return getTopBottomChartProps(config, data as TopBottomData)
          default:
            return getChartProps(config, data as CompassAPIMultiSeriesChartData)
        }
      },
    }

    const isQuarterXAxis = (chart: ChartConfig['chart']) =>
      chart === 'historical_surprise' || chart === 'growth_yoy_reported'

    const getQuarterlyChartProps = (
      config: ChartConfig,
      data: CategoryChartData
    ) => {
      const series = [
        {
          data: data.data.map(({ label, value }) => [
            label,
            config.chart === 'by_ticker' ? value * 100 : value,
          ]),
          id: 'series-0',
          marker: { enabled: false },
          name: '',
          type: 'column',
          tooltip: {
            pointFormatter: magnitudePointFormatter({
              unit: config.chart === 'by_ticker' ? '%' : '',
            }),
          },
          yAxis: 'default',
        },
      ]

      const xAxis = [
        {
          type: 'category' as const,
          categories: data.data.map(({ label }) => label),
        },
      ]

      const creditsConfig = getDataSources(data.data_sources)

      return {
        id: Context.getChartId(config),
        title:
          data.title ?? config.sector
            ? CHART_TITLES[config.chart].replace(
                '<Sector>',
                config.sector ?? ''
              )
            : data.title ?? config.quarter
            ? CHART_TITLES[config.chart].replace(
                '<Quarter>',
                config.quarter ?? ''
              )
            : CHART_TITLES[config.chart],
        options: mergeDeepAll([
          creditsConfig,
          commonChartOptions,
          {
            ...(config.chart === 'by_ticker' ? { chart: { type: 'bar' } } : {}),
            series,
            yAxis:
              config.chart === 'by_ticker' ? PERCENTAGE_Y_AXIS : DEFAULT_Y_AXIS,
            xAxis,
            tooltip: { shared: true, split: false },
          },
        ]),
        stockTools: true,
        useRegularChart: true,
      }
    }

    const getTopBottomChartProps = (
      config: ChartConfig,
      data: TopBottomData
    ) => {
      const series = [
        {
          data: data.data
            ? [
                ...data.data.top,
                { label: '', value: 0 },
                ...data.data.bottom,
              ].map((column, idx) => ({
                name: column.label,
                y: column.value,
                color:
                  column.value >= 0
                    ? '#30ba09' // success.main
                    : '#f4643d', // error.light
                ...(idx === data.data.top.length
                  ? { dataLabels: { enabled: false } }
                  : {}),
              }))
            : [],
          dataLabels: { enabled: true, format: '{point.y:.2f}' },
          color: Palette.green,
          showInLegend: false,
          yAxis: 'primary',
        },
      ]
      const creditsConfig = getDataSources(data.data_sources)

      const tooltip = { shared: true, split: undefined, valueDecimals: 2 }

      const xAxis = [
        {
          plotLines: [
            data
              ? {
                  dashStyle: 'LongDash' as const,
                  value: data.data.top.length,
                  label: {
                    formatter: () => {
                      const top =
                        data && data.data.top.length > 0
                          ? `Top ${data.data.top.length}`
                          : ''
                      const bottom =
                        data && data.data.bottom.length > 0
                          ? `Bottom ${data.data.bottom.length}`
                          : ''
                      return `${top}<br />${bottom}`
                    },
                  },
                  zIndex: 10,
                }
              : {},
          ],
          type: 'category' as const,
        },
      ]

      const yAxis = [
        {
          id: 'primary',
          title: { text: '' },
          labels: { format: '{value} mi' },
        },
      ]

      return {
        id: Context.getChartId(config),
        title: config.title,
        options: mergeDeepAll([
          creditsConfig,
          {
            chart: { type: 'bar' },
            tooltip,
            series,
            stockTools: { gui: { enabled: false } },
            xAxis,
            yAxis,
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    const getChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: config.chart !== 'coverage_factor',
          chartTitle: CHART_TITLES[config.chart],
          convertToPercentages:
            config.chart === 'coverage_factor'
              ? data.data.map((s) => s.name.includes('ponderated'))
              : Array(data.data.length).fill(true),
          ...(isQuarterXAxis(config.chart)
            ? {
                tooltip: { xDateFormat: "Q%q'%y" },
                xAxis: { labels: { format: "{value:Q%q'%y}" } },
              }
            : {
                tooltip: { xDateFormat: '%B %Y' },
                xAxis: { labels: { format: '{value:%Y}' } },
              }),
          ...(config.chart === 'coverage_factor'
            ? {
                yAxis: [
                  { id: 'primary', labelSuffix: '%' },
                  { id: 'secondary', opposite: true },
                ],
              }
            : {}),
          ...(config.chart === 'historical_surprise' ||
          config.chart === 'growth_yoy_reported'
            ? { yAxis: [{ labelSuffix: '%', max: 40, min: -40 }] }
            : {}),
        },
        {
          ...(config.chart === 'coverage_factor'
            ? {
                yAxis: data.data.map((s) =>
                  s.name.includes('ponderated') ? 'primary' : 'secondary'
                ),
              }
            : {}),
          ...(config.chart === 'percentage_positive_surprises'
            ? { type: Array(data.data.length).fill('spline') }
            : {}),
        }
      )(config, formatSeriesNames(data))
  }

  export namespace SellSideSentiments {
    type SellSideSentimentsChartId =
      | 'historical_sentiment'
      | 'quarter_sector_scores'
      | 'quarter_ticker_scores'

    const SELL_SIDE_SENTIMENT_CHART_PATHS: Record<
      SellSideSentimentsChartId,
      string
    > = {
      historical_sentiment: '/sell-side-sentiments/historical-sentiment',
      quarter_sector_scores: '/sell-side-sentiments/quarter-sector-scores',
      quarter_ticker_scores: '/sell-side-sentiments/quarter-ticker-scores',
    }

    const CHART_TITLES: Record<SellSideSentimentsChartId, string> = {
      historical_sentiment: 'IBOV historical sentiment',
      quarter_sector_scores: 'Sector sentiments - <Quarter>',
      quarter_ticker_scores: '<Sector> Sentiment - By ticker',
    }

    const CONTEXT_ID = 'equity:surprise:sell_side_sentiment'
    const SURPRISE_CHARTS_PATH = '/api/charts/surprise'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      context: typeof CONTEXT_ID
      chart: SellSideSentimentsChartId
      title?: string
      basketId: string
      basketUsername: string
      weightsId?: string
      quarter?: string
      classificationName?: string
      isBarChart?: boolean
    }

    export interface CategoryChartData extends CompassAPIDataBase {
      title?: string
      data: CategoryPoint[]
      data_sources: DataSource[] | null
    }

    type ChartData = CompassAPIMultiSeriesChartData | CategoryChartData

    const getChartData = async (
      config: ChartConfig,
      modifier: (config: AxiosRequestConfig) => AxiosRequestConfig = (conf) =>
        conf
    ) => {
      const response = (await axios(
        modifier({
          url:
            SURPRISE_CHARTS_PATH +
            SELL_SIDE_SENTIMENT_CHART_PATHS[config.chart],
          method: 'POST',
          data: {
            basket_id: config.basketId,
            basket_username: config.basketUsername,
            ...(config.weightsId ? { weights_id: config.weightsId } : {}),
            ...(config.quarter ? { quarter: config.quarter } : {}),
            ...(config.classificationName
              ? { classification_name: config.classificationName }
              : {}),
          },
        })
      )) as AxiosResponse<ChartData>

      return response.data
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.chart}:${hash(config)}`,
      getChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'historical_sentiment':
            return getChartProps(config, data as CompassAPIMultiSeriesChartData)
          case 'quarter_sector_scores':
          case 'quarter_ticker_scores':
            return getQuarterScoresProps(config, data as CategoryChartData)
        }
      },
    }

    const getChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(Context, {
        chartTitle: CHART_TITLES[config.chart],
      })(config, formatSeriesNames(data))

    const getQuarterScoresProps = (
      config: ChartConfig,
      data: CategoryChartData
    ) => {
      const series = [
        {
          data: data.data.map(({ label, value }) => [label, value]),
          id: 'series-0',
          marker: { enabled: false },
          name: '',
          type: 'column',
          tooltip: { pointFormatter: magnitudePointFormatter() },
          yAxis: 'default',
        },
      ]

      const xAxis = [
        {
          type: 'category' as const,
          categories: data.data.map(({ label }) => label),
        },
      ]

      const creditsConfig = getDataSources(data.data_sources)

      return {
        id: Context.getChartId(config),
        title:
          data.title ?? config.classificationName
            ? CHART_TITLES[config.chart].replace(
                '<Sector>',
                config.classificationName ?? ''
              )
            : config.chart === 'quarter_sector_scores'
            ? CHART_TITLES[config.chart].replace(
                '<Quarter>',
                config.quarter ?? ''
              )
            : CHART_TITLES[config.chart],
        options: mergeDeepAll([
          creditsConfig,
          commonChartOptions,
          {
            ...(config.isBarChart ? { chart: { type: 'bar' } } : {}),
            series,
            tooltip: { shared: true, split: false },
            xAxis,
            yAxis: DEFAULT_Y_AXIS,
          },
        ]),
        stockTools: true,
        useRegularChart: true,
      }
    }
  }
}
